import { IonButton, IonContent, IonIcon, IonLabel, IonPage } from '@ionic/react'
import { GetExploreResultsParamsOrderBy } from 'domain/usecases/get-explore-results'
import { ListPatientsParams } from 'domain/usecases/list-patients'
import { PatientsService } from 'infra/services/patients-service'
import { ReportsService } from 'infra/services/reports-service'
import { UserService } from 'infra/services/user-service'
import { addOutline } from 'ionicons/icons'
import { DebounceInput } from 'presentation/components'
import Footer from 'presentation/components/footer/footer'
import ExploreHeading from 'presentation/organisms/explore-heading/explore-heading'
import { Header } from 'presentation/organisms/header/header'
import React, { useEffect, useRef, useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { ModalNewReport } from '../modal-new-report/modal-new-report'
import { dashboardAtom } from './dashboard-atom'
import './dashboard.scss'
import { HeadingFilter, headingModalReport } from './organisms/heading/heading-atom'
import ModalFilterStatePatients from './organisms/modal-filter-patients/modal-filter-patients'
import PatientsList from './organisms/patients-list/patients-list'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { ReportStatus } from 'domain/usecases/get-report'
import ReportsList from './organisms/reports-list/reports-list'
import ModalFilterStateReports from './organisms/modal-filter-reports/modal-filter-reports'
import { ClinicsService } from 'infra/services/clinics-service'
import { GenericSelector } from 'infra/services/usecases/generic-selector'
import { makeCacheLocalStorage } from 'main/factories/cache/cache-local-storage-factory'

interface DashboardPageProps {
  userService: UserService
  patientsService: PatientsService
  reportsService: ReportsService
  clinicsService: ClinicsService
}

const DashboardPage: React.FC<DashboardPageProps> = (props) => {
  const setState = useSetRecoilState(dashboardAtom)
  const filter = {
    search: '',
    orderBy: 'changed',
    list: {},
  }
  const modalNewReport = useRef<HTMLIonModalElement>(null)
  const [modalReportOpener, setModalReportOpener] = useRecoilState(headingModalReport)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const modalFilterPatients = useRef<HTMLIonModalElement>(null)
  const modalFilterReports = useRef<HTMLIonModalElement>(null)

  const [orderBy, setOrderBy] = useState('changed')
  const [search, setSearch] = useState('')
  const [chips, setChips] = useState<HeadingFilter>({})
  const [user, setUser] = useState<UserInfo | null>(null)
  const [clinic, setClinic] = useState<GenericSelector | null>(null)

  const handleDeleteChip = (chip: string) => {
    const currentChips = JSON.parse(JSON.stringify(chips))

    delete currentChips[chip]

    setChips(currentChips)
  }

  const getPage = (params: { pageNumber: number }, userInfo?: UserInfo) => {
    setFilters()
    if (userInfo?.role === UserRole.Oncologist || user?.role === UserRole.Oncologist || userInfo?.role === UserRole.MedicalAssistant || user?.role === UserRole.MedicalAssistant) {
      getPatients(params)
    } else if (userInfo?.role === UserRole.Reviewer || user?.role === UserRole.Reviewer) {
      getReports(params)
    }
  }

  const getClinicInfo = (clinic_id: string) => {
    props.clinicsService.getClinicInfo(clinic_id).then((clinic) => {
      const localStorage = makeCacheLocalStorage()
      localStorage.set('clinic-name', clinic.name)
      setClinic(clinic)
    })
      .catch(() => setState((s) => ({ ...s, clinic: null })))
  }

  const getReports = (params: { pageNumber: number }) => {
    setState((s) => ({ ...s, isLoadingReports: true }))
    const paramsAll = {
      page: params.pageNumber,
      size: 10,
      report_status: [ReportStatus.back_to_review, ReportStatus.processed, ReportStatus.processing],
      ...filter.list,
    }

    props.reportsService.getReports({ ...paramsAll }).then((page) => {
      setState((s) => ({
        ...s,
        reports: page.items,
        pageOptions: {
          current: params.pageNumber,
          quantity: Math.ceil(page.total / page.size),
          size: page.total,
        },
      }))
    })
      .catch(() => setState((s) => ({ ...s, reports: [] })))
      .finally(() => setState((s) => ({ ...s, isLoadingReports: false })))
  }

  const getPatients = (params: { pageNumber: number }) => {
    setState((s) => ({ ...s, isLoadingPatients: true }))
    const paramsAll = {
      page: params.pageNumber,
      size: 10,
      report_status: [ReportStatus.reviewed, ReportStatus.back_to_review, ReportStatus.processing, ReportStatus.processed],
      ...filter.list,
    }

    props.patientsService
      .getPatients({
        ...paramsAll,
      }).then((page) => {
        setState((s) => ({
          ...s,
          patients: page.items.map((item) => ({
            ...item,
          })),
          pageOptions: {
            current: params.pageNumber,
            quantity: Math.ceil(page.total / page.size),
            size: page.total,
          },
        }))
      })
      .catch(() => setState((s) => ({ ...s, patients: [] })))
      .finally(() => setState((s) => ({ ...s, isLoadingPatients: false })))
  }

  const setFilters = () => {
    filter.list = {
      search: search,
      ...(chips?.cancerType ? { cancer_type: [chips?.cancerType.label] } : {}),
      ...(chips.genomicVariation ? { genomic_variant: [chips.genomicVariation.label] } : {}),
      ...(chips.status ? { status: [chips.status.value] } : {}),
      ...(chips.treatment ? { treatment: [chips.treatment.value] } : {}),
      ...(chips.patient ? { name: chips.patient.label } : {}),
      ...(chips.oncologist_id ? { oncologist_id: chips.oncologist_id.value } : {}),
      order_by: {
        field: orderBy || 'changed',
        order: orderBy === 'changed' ? 'DESC' : 'ASC',
      },
    } as Partial<ListPatientsParams>
  }

  useEffect(() => {
    props.userService.getUserInfo().then((user) => {
      const localStorage = makeCacheLocalStorage()
      if(!localStorage.get('clinic-name'))
        getClinicInfo(user.clinic)
      
      setUser(user)
      getPage({ pageNumber: 1 }, user)
    })
  }, [search, chips, orderBy])

  return (
    <IonPage className="dashboard-wrapper">
      <Header userService={props.userService} tab={user?.role === UserRole.Reviewer ? 'reports' : 'patients'} />
      <IonContent>
        <ExploreHeading
          headingTitle={user?.role === UserRole.Reviewer ? 'Reports' : 'Patients'}
          onFilterButtonClick={() => setIsFilterOpen(true)}
          slotSearch={
            <>
              <IonLabel position="stacked">Search</IonLabel>
              <DebounceInput
                value={search}
                debounceTime={500}
                onDebounceChange={(search) => setSearch(search)}
                placeholder={user?.role === UserRole.Reviewer ? 'Type a Name, MRN or Report ID to filter the list' : 'Type a Name or an MRN to filter the list'}
              />
            </>
          }
          slotAction={
            user?.role === UserRole.Oncologist || user?.role === UserRole.MedicalAssistant ? (
              <IonButton
                color="light"
                id="trigger-modal-report"
                onClick={() => setModalReportOpener((s) => ({ ...s, isOpen: true }))}
              >
                <IonIcon icon={addOutline} slot="start" />
                New Report
              </IonButton>
            ) : (
              <></>
            )
          }
          slotSearchHide={false}
          onDeleteSearch={() => setSearch('')}
          //
          tokens={[]}
          onDeleteToken={() => null}
          onClearTokens={() => null}
          //
          chips={chips}
          onDeleteChip={(chip) => handleDeleteChip(chip)}
          onClearAll={() => setChips({})}
          //
          orderByOptions={
            [
              { label: 'Last Update', value: 'changed' },
              { label: 'Patient Name (A-Z)', value: 'name_asc' },
              { label: 'Patient Name (Z-A)', value: 'name_desc' },
              { label: 'Oncologist (A-Z)', value: 'oncologist_asc' },
              { label: 'Oncologist (Z-A)', value: 'oncologist_desc' },
            ]
          }
          orderByValue={orderBy}
          orderByPlaceHolder={'Select'}
          onOrderByValueChange={(value) => setOrderBy(value as GetExploreResultsParamsOrderBy)}
        />

        {
          (user?.role === UserRole.Oncologist || user?.role === UserRole.MedicalAssistant) && (
            <PatientsList
              onGetPage={(n) => getPage({ pageNumber: n }, user)}
              search={search}
              userInfo={user as UserInfo}
              filters={chips}
            />
          )
        }
        {
          user?.role === UserRole.Reviewer && (
            <ReportsList
              onGetPage={(n) => getPage({ pageNumber: n }, user)}
              search={search}
              filters={chips} />
          )
        }

        <ModalNewReport
          trigger="trigger-modal-report"
          modal={modalNewReport}
          isOpen={modalReportOpener.isOpen}
          reportsService={props.reportsService}
          patientsService={props.patientsService}
          clinicsService={props.clinicsService}
          userInfo={user as UserInfo}
          onDidDismiss={(params) => {
            if (params && params.detail.data.shouldReload) getPage({ pageNumber: 1 })
            setModalReportOpener((s) => ({ ...s, isOpen: false }))
          }}
          notNeededToConfirm={false}
        />

        {
          (user?.role === UserRole.Reviewer) && (
            <ModalFilterStateReports
              trigger="trigger-modal-reports"
              modal={modalFilterReports}
              isOpen={isFilterOpen}
              patientsService={props.patientsService}
              reportsService={props.reportsService}
              clinicService={props.clinicsService}
              userInfo={user as UserInfo}
              onDidDismiss={(filter) => {
                const data = filter.detail.data
                setIsFilterOpen(false)
                if (data) setChips(data)
              }}
              filtersApplied={chips}
            />
          )
        }

        {
          (user?.role === UserRole.Oncologist || user?.role === UserRole.MedicalAssistant) && (
            <ModalFilterStatePatients
              trigger="trigger-modal-patients"
              modal={modalFilterPatients}
              isOpen={isFilterOpen}
              patientsService={props.patientsService}
              userInfo={user as UserInfo}
              clinicService={props.clinicsService}
              onDidDismiss={(filter) => {
                const data = filter.detail.data
                setIsFilterOpen(false)
                if (data) setChips(data)
              }}
              filtersApplied={chips}
            />
          )
        }
      </IonContent>
      <Footer />
    </IonPage>
  )
}

export default DashboardPage
